import { gql, useQuery } from '@apollo/client';
import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import * as React from 'react';

import { Loading } from '@/imports/ui/chakra/components/Loading';

import {
  AdminDeactivatedUserLogsModal_adminDeactivatedUserLogs,
  AdminDeactivatedUserLogsModal_adminDeactivatedUserLogsVariables,
} from './graphql-types/AdminDeactivatedUserLogsModal_adminDeactivatedUserLogs';

const GET_ADMIN_DEACTIVATED_USER_LOGS = gql`
  query AdminDeactivatedUserLogsModal_adminDeactivatedUserLogs(
    $input: AdminDeactivatedUserLogsInput
  ) {
    adminDeactivatedUserLogs(input: $input) {
      results {
        _id
        name
        email
        integrationId
        deactivatedByName
        deactivatedOn
      }
    }
  }
`;

const tableHeaderProps = {
  fontSize: 'xs',
  fontWeight: 'semibold',
  color: 'gray.600',
  textTransform: 'uppercase' as const,
  letterSpacing: 'wider',
  borderColor: 'gray.200',
  bg: 'blue.50',
  py: 3,
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const AdminDeactivatedUserLogsModal: React.FC<Props> = ({
  isOpen,
  onClose,
}) => {
  const { data, loading, error, refetch } = useQuery<
    AdminDeactivatedUserLogsModal_adminDeactivatedUserLogs,
    AdminDeactivatedUserLogsModal_adminDeactivatedUserLogsVariables
  >(GET_ADMIN_DEACTIVATED_USER_LOGS, {
    variables: { input: { limit: 500, offset: 0 } },
    skip: !isOpen,
    fetchPolicy: 'network-only',
  });

  React.useEffect(() => {
    if (isOpen) {
      refetch();
    }
  }, [isOpen, refetch]);

  const logs = data?.adminDeactivatedUserLogs.results ?? [];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      size="3xl"
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent maxH="min(520px, 85vh)">
        <ModalCloseButton />
        <ModalHeader pb={3} pt={5} px={6}>
          <Text fontSize="lg" fontWeight="bold" color="gray.900">
            Archived Logs
          </Text>
          <Text fontSize="sm" fontWeight="normal" color="gray.600" mt="1">
            Accounts archived from Tradewing contacts and directory.
          </Text>
        </ModalHeader>
        <ModalBody px={6} pt={0} pb={3}>
          {loading && <Loading />}
          {error && (
            <Text color="red.500" textAlign="center" fontSize="sm">
              {error.message}
            </Text>
          )}
          {!loading && !error && (
            <Box
              borderWidth="1px"
              borderColor="gray.200"
              borderRadius="md"
              overflow="hidden"
            >
              <Box maxH="240px" overflowY="auto" overflowX="auto">
                <Table size="sm" variant="simple">
                  <Thead position="sticky" top={0} zIndex={1}>
                    <Tr>
                      <Th {...tableHeaderProps}>Name</Th>
                      <Th {...tableHeaderProps}>Email</Th>
                      <Th {...tableHeaderProps}>External ID</Th>
                      <Th {...tableHeaderProps}>Archived By</Th>
                      <Th {...tableHeaderProps}>Archived On</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {logs.length === 0 ? (
                      <Tr>
                        <Td
                          colSpan={5}
                          textAlign="center"
                          py={8}
                          color="gray.500"
                          fontSize="sm"
                        >
                          No deactivated accounts yet.
                        </Td>
                      </Tr>
                    ) : (
                      logs.map((log) => (
                        <Tr
                          key={log._id}
                          borderBottomWidth="1px"
                          borderColor="gray.100"
                        >
                          <Td
                            py={2.5}
                            fontSize="sm"
                            fontWeight="medium"
                            color="gray.800"
                          >
                            {log.name}
                          </Td>
                          <Td py={2.5} fontSize="sm" color="gray.700">
                            {log.email ?? '—'}
                          </Td>
                          <Td py={2.5} fontSize="sm" color="gray.700">
                            {log.integrationId ?? '—'}
                          </Td>
                          <Td py={2.5} fontSize="sm" color="gray.800">
                            {log.deactivatedByName}
                          </Td>
                          <Td
                            py={2.5}
                            fontSize="sm"
                            color="gray.800"
                            whiteSpace="nowrap"
                          >
                            {dayjs(log.deactivatedOn).format('MMM D, YYYY')}
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>
            </Box>
          )}
        </ModalBody>
        <ModalFooter px={6} py={4}>
          <Button colorScheme="blue" size="md" onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
